import type { AnyFn } from "@vueuse/core";
import type { V2SectionInput } from "./types";
import type { ScheduleTime } from "@/types/task";
import { isPast } from "date-fns";
import type { IContentInputBase } from "@/types/v2";

interface BottomSectionInputState {
  key: "DEFAULT" | "teleport" | string;
  inputs: V2SectionInput[];
  buttonLabel: string;
  isValid: boolean;
  buttonType: "next" | "send";
  done?: boolean;
  errors?: string[];
  saving: boolean;
  hidden?: boolean;
  canSkip?: boolean;
  canGoBack?: boolean;
  scheduling?: boolean;
  scheduleData?: ScheduleTime;
  inputValues: Record<string, any>;
  skipStep?: boolean;
  onSubmitFn: AnyFn;
  onSkipFn?: AnyFn;
  onBackFn?: AnyFn;
}

export const DEFAULT_V2_SUBMIT_EVENT = "v2InputSubmit";
export const DEFAULT_V2_SKIP_EVENT = "v2InputSkip";
export const DEFAULT_V2_BACK_EVENT = "v2InputBack";
export const INSTANT_V2_MESSAGE_EVENT = "instantV2Message";

const event = new Event(DEFAULT_V2_SUBMIT_EVENT);
const skipEvent = new CustomEvent(DEFAULT_V2_SKIP_EVENT, {
  detail: { skipStep: true },
});
const backEvent = new CustomEvent(DEFAULT_V2_BACK_EVENT, {
  detail: { back: true },
});

function defaultSubmitHandler() {
  if (typeof document === "undefined") return;
  document.dispatchEvent(event);
}

function defaultSkipHandler() {
  if (typeof document === "undefined") return;
  document.dispatchEvent(skipEvent);
}

function defaultBackHandler() {
  if (typeof document === "undefined") return;
  document.dispatchEvent(backEvent);
}

export function sendInstantV2Message(payload: { text: string }) {
  if (typeof document === "undefined") return;
  document.dispatchEvent(new CustomEvent(INSTANT_V2_MESSAGE_EVENT, { detail: payload }));
}

const defaultState = (): BottomSectionInputState => ({
  key: "DEFAULT",
  buttonLabel: "Send",
  buttonType: "send",
  inputs: [],
  isValid: true,
  errors: [],
  done: false,
  hidden: false,
  saving: false,
  scheduling: false,
  canGoBack: false,
  inputValues: {},
  skipStep: false,
  onSubmitFn: defaultSubmitHandler,
});

export const defaultV2Height = 256;
export const defaultV2MaxHeight = Math.min(window.innerHeight / 2, 500);
export const defaultV2MinHeight = 1;

export const useV2InputStore = defineStore("v2InputStore", () => {
  const stateQueue = ref<BottomSectionInputState[]>([]);
  // mutated in store
  const state = ref<BottomSectionInputState>(defaultState());
  const bottomSectionHeight = useLocalStorage("bottomSectionHeight", defaultV2Height);
  const bottomSectionMinHeight = ref(defaultV2MinHeight);
  const bottomSectionMaxHeight = ref(defaultV2MaxHeight);
  const basicInputValue = ref("");
  const basicInputImages = ref<string[]>([]);

  // expose read-only value
  const inputs = computed(() => state.value.inputs);
  const hasInputs = computed(() => inputs.value.length > 0);

  const hasInvalidSChedule = computed(
    () =>
      state.value.scheduleData &&
      state.value.scheduleData.type === "date" &&
      isPast(new Date(`${state.value.scheduleData.value}.000Z`))
  );

  function toggleBottomSectionHeight(val?: number) {
    if (typeof val === "number") {
      bottomSectionHeight.value = val === -1 ? 0 : val;
      if (val === -1) {
        bottomSectionMaxHeight.value = 1;
        bottomSectionMinHeight.value = 0;
      } else {
        bottomSectionMaxHeight.value = defaultV2MaxHeight;
        bottomSectionMinHeight.value = defaultV2MinHeight;
      }
      return;
    }
    if (bottomSectionHeight.value < defaultV2Height) {
      bottomSectionHeight.value = defaultV2Height;
      bottomSectionMaxHeight.value = defaultV2MaxHeight;
      bottomSectionMinHeight.value = defaultV2MinHeight;
    } else {
      bottomSectionHeight.value = 0;
      bottomSectionMinHeight.value = 0;
    }
  }
  function enqueueInputState(s: BottomSectionInputState) {
    if (!s) return;
    if (!stateQueue.value.length && state.value.key === "DEFAULT") {
      stateQueue.value.push(s);
      dequeueInputState();
    } else {
      if (state.value?.key === s?.key) {
        const oldInputValues = state.value.inputValues || {};
        const newInputValues = s.inputValues || {};
        for (const key in newInputValues) {
          newInputValues[key] = oldInputValues[key] || newInputValues[key] || "";
        }
        s.inputValues = newInputValues;
      }
      stateQueue.value.push(s);
    }
  }

  function dequeueInputState() {
    if (!stateQueue.value.length) {
      setDefaultState();
      return;
    }
    const newState = stateQueue.value.pop();
    if (newState) state.value = newState;
  }

  function setDefaultState() {
    state.value = defaultState();
  }

  const currentStateHelpers = {
    setValidation(isValid: boolean) {
      state.value.isValid = isValid;
    },
    setButtonState(btn: {
      label: string;
      buttonType: BottomSectionInputState["buttonType"];
    }) {
      state.value.buttonLabel = btn.label;
      state.value.buttonType = btn.buttonType;
    },
    setSubmitFunction(fn: AnyFn) {
      state.value.onSubmitFn = fn;
    },
    setErrors(errors: string[]) {
      state.value.errors = errors;
    },
    addError(e: string) {
      state.value.errors = [...(state.value.errors || []), e];
    },
    dismissActiveError() {
      if (!state.value.errors?.length) return;
      if (state.value.errors.length === 1) {
        state.value.errors = [];
        return;
      }
      state.value.errors = state.value.errors.slice(1);
    },
    setDone() {
      state.value.done = true;
      state.value.saving = false;
      setTimeout(() => {
        state.value.done = false;
      }, 1.2 * SECOND);
    },
    setInputs(inputs: V2SectionInput[]) {
      if (inputs && inputs.length) state.value.inputs = inputs;
    },
    saveStep() {
      if (!state.value.onSubmitFn) return;
      state.value.onSubmitFn();
      if (state.value.key !== "DEFAULT") defaultSubmitHandler(); // emit event
    },
    skipStep() {
      if (!state.value.canSkip) return;
      if (state.value.onSkipFn) state.value.onSkipFn();
      else defaultSkipHandler();
    },
    goBack() {
      if (!state.value.canGoBack) return;
      if (typeof state.value.onBackFn === "function") state.value.onBackFn();
      else defaultBackHandler();
    },
    setLoading() {
      state.value.done = false;
      state.value.saving = true;
    },
    hideInputs() {
      state.value.hidden = true;
    },
    showInputs() {
      state.value.hidden = false;
    },
    toggleScheduling(value?: boolean) {
      if (value === undefined) {
        state.value.scheduling = !state.value.scheduling;
        return;
      }
      state.value.scheduling = value;
    },
  };

  function getFormattedBasicInputAndImages() {
    const formattedContent: IContentInputBase<"paragraph" | "image">[] = [];
    formattedContent.push({
      type: "paragraph",
      id: "1",
      data: {
        text: basicInputValue.value,
      },
    });
    const images = basicInputImages.value;
    if (images.length) {
      formattedContent.push(
        ...(images.map((url, i) => ({
          type: "image",
          id: (i + 2).toString(),
          data: {
            url,
          },
        })) as IContentInputBase<"image">[])
      );
    }
    return formattedContent;
  }

  return {
    state,
    inputs,
    hasInputs,
    hasInvalidSChedule,
    toggleBottomSectionHeight,
    bottomSectionHeight,
    bottomSectionMaxHeight,
    bottomSectionMinHeight,
    dequeueInputState,
    enqueueInputState,
    setDefaultState,
    currentStateHelpers,
    basicInputImages,
    basicInputValue,
    getFormattedBasicInputAndImages,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useV2InputStore, import.meta.hot));
}
